/**
 * Contextual Modify tab — one-shot action backends for imported CAD drawings
 * and raster images. Composed by useContextualActions().
 */
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useBIMStore } from '../../state/useBIMStore';
import { notify } from '../../utils/notify';
import { getSelectedElements } from '../../utils/contextualModelUtils';
import { isImportedCad } from '../../tools/contextual/ContextualActionTools';
import i18n from '../../i18n';

interface CadLine {
    start?: [number, number, number];
    end?: [number, number, number];
    points?: [[number, number, number], [number, number, number]];
    layer?: string;
}

/** Absolute world endpoints of a CAD line record (parsed payloads are local
 *  to the element origin; legacy start/end payloads are absolute). */
function lineEndpoints(ln: CadLine, origin?: number[]) {
    const off = (ln.points && origin) ? origin : [0, 0, 0];
    const a = ln.points ? ln.points[0] : (ln.start ?? [0, 0, 0]);
    const b = ln.points ? ln.points[1] : (ln.end ?? [0, 0, 0]);
    return {
        start: [a[0] + off[0], a[1] + off[1], a[2] + off[2]] as [number, number, number],
        end: [b[0] + off[0], b[1] + off[1], b[2] + off[2]] as [number, number, number],
    };
}

export function useImportContextualActions() {
    /**
     * Explode (Full): convert every CAD line into a free Model Line element and
     * remove the import (BIM's full explode).
     */
    const explodeFull = useCallback(() => {
        const bim = useBIMStore.getState();
        const cads = getSelectedElements().filter(isImportedCad);
        if (cads.length === 0) {
            notify(i18n.t('tools.notify.selectAnImportedCadDrawingTo'), 'warning');
            return;
        }
        let total = 0;
        cads.forEach(cad => {
            const g = (cad as any).geometryData;
            const lines: CadLine[] = Array.isArray(g?.lines) ? g.lines : [];
            if (lines.length === 0) {
                notify(i18n.t('tools.notify.thisImportCarriesNoExplodableLine'), 'warning');
                return;
            }
            const origin = g.origin as number[] | undefined;
            lines.forEach(ln => {
                const abs = lineEndpoints(ln, origin);
                const len = Math.hypot(
                    abs.end[0] - abs.start[0], abs.end[1] - abs.start[1], abs.end[2] - abs.start[2]
                );
                bim.addElement({
                    id: uuidv4(),
                    category: 'ModelLines',
                    type: 'Model Line',
                    parameters: {
                        'Length': len.toFixed(2) + ' m',
                        'Source Layer': ln.layer ?? '0',
                    },
                    constraints: [],
                    geometryData: { start: abs.start, end: abs.end },
                } as any);
            });
            bim.removeElement(cad.id);
            total += lines.length;
        });
        if (total > 0) notify(i18n.t('tools.notify.cadExploded', { count: total }), 'success');
    }, []);

    /** Delete Layers: drop one CAD layer from every selected import. */
    const deleteLayers = useCallback(() => {
        const bim = useBIMStore.getState();
        const cads = getSelectedElements().filter(isImportedCad);
        if (cads.length === 0) {
            notify(i18n.t('tools.notify.selectAnImportedCadDrawingTo2'), 'warning');
            return;
        }
        // Collect the layer names present in the selection.
        const layers = new Set<string>();
        cads.forEach(cad => {
            const lines: CadLine[] = (cad as any).geometryData?.lines ?? [];
            lines.forEach(ln => layers.add(ln.layer ?? '0'));
        });
        if (layers.size === 0) {
            notify(i18n.t('tools.notify.noLayersFoundInTheSelected'), 'info');
            return;
        }
        const first = [...layers][0];
        // BIM opens a layer picker; without a modal surface we remove the
        // dominant (first) layer and report what remains.
        cads.forEach(cad => {
            const g = (cad as any).geometryData;
            const lines: CadLine[] = g?.lines ?? [];
            const kept = lines.filter(ln => (ln.layer ?? '0') !== first);
            if (kept.length === 0) {
                bim.removeElement(cad.id);
            } else {
                bim.updateElement(cad.id, { geometryData: { ...g, lines: kept } } as any);
            }
        });
        notify(i18n.t('tools.notify.cadLayerDeleted', { name: first, count: layers.size, remaining: [...layers].filter(l => l !== first).join(', ') || 'none' }), 'success');
    }, []);

    /** Foreground / Background draw order for CAD imports and raster images. */
    const setDrawOrder = useCallback((order: 'Foreground' | 'Background') => {
        const bim = useBIMStore.getState();
        const targets = getSelectedElements().filter(e =>
            isImportedCad(e) || e.category === 'Raster Images' || e.category === 'CAD Links');
        if (targets.length === 0) {
            notify(i18n.t('tools.notify.selectAnImportedCadDrawingOr'), 'warning');
            return;
        }
        targets.forEach(el => {
            bim.updateElement(el.id, {
                parameters: { ...el.parameters, 'Draw Order': order },
            } as any);
        });
        notify(i18n.t('tools.notify.drawOrderSet', { order: order }), 'success');
    }, []);

    const foreground = useCallback(() => setDrawOrder('Foreground'), [setDrawOrder]);
    const background = useCallback(() => setDrawOrder('Background'), [setDrawOrder]);

    return { explodeFull, deleteLayers, foreground, background };
}
